import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState
} from "react";

import { Cookie } from "./cookie";

type OneTrustContextType = {
    analyticsOptOut: boolean;
};

/**
 * Check if OneTrust wrapper has performance cookie enabled.
 * This works inconjunction with OneTrustProvider, which checks for updates to the OneTrust cookie.
 * If the cookie is not found or performance isn't explicitly set to not track, default to tracking.
 */
export const isPerformanceOnetrustEnabled = (): boolean => {
    const optanonConsentCookie = Cookie.readCookie("OptanonConsent");
    if (optanonConsentCookie === null) {
        return true;
    }

    const params = new URLSearchParams(optanonConsentCookie);
    const groups = params.get("groups");
    if (groups === null) {
        return true;
    }

    const groupPairs = groups.split(",");
    return groupPairs.includes("2:1");
};

const OneTrustContext = createContext<OneTrustContextType>({
    analyticsOptOut: true
});

/**
 * Custom hook to return OneTrustContext.
 * @returns @type {OneTrustContextType}
 */
export const useOneTrustContext = (): OneTrustContextType => {
    const context = useContext(OneTrustContext);
    if (!context) {
        throw new Error(
            "calls to useOneTrustContext must be wrapped in <OneTrustProvider>"
        );
    }

    return context;
};

/**
 * Check `window.OnetrustActiveGroups` every quater of sec to see if OneTrust has been initialized.
 * OneTrust script is referenced from the head HTML tag and sets the `window.OnetrustActiveGroups` and `OptanonConsent` cookie once it has been initialized.
 */
export const OneTrustProvider = ({ children }: PropsWithChildren) => {
    const [analyticsOptOut, setAnalyticsOptOut] = useState(true);

    useEffect(() => {
        const checkOnetrustActiveGroups = () => {
            if (window.OnetrustActiveGroups === undefined) {
                return;
            }

            const currentAnalyticsOptOut =
                !window.OnetrustActiveGroups.includes(",2,");
            if (analyticsOptOut !== currentAnalyticsOptOut) {
                setAnalyticsOptOut(currentAnalyticsOptOut);
            }

            /**
             * Clear the interval once the OneTrust has been initialized and only recheck every 1 sec if the optout status is still the default (true).
             */
            clearInterval(setIntervalId);
            if (currentAnalyticsOptOut) {
                setIntervalId = setInterval(checkOnetrustActiveGroups, 1000);
            }
        };

        let setIntervalId = setInterval(checkOnetrustActiveGroups, 250);
        return () => {
            clearInterval(setIntervalId);
        };
    }, []);

    return (
        <OneTrustContext.Provider value={{ analyticsOptOut }}>
            {children}
        </OneTrustContext.Provider>
    );
};
